import { useCallback, useState } from 'react';
import { log } from '../../../Utilities/logging';

export function useLocalStorage<T>(key: string, initialValue: T) {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState<T>(() => {
        if (typeof window === "undefined") {
            return initialValue;
        }
        try {
            // Get from local storage by key
            const item = window.sessionStorage.getItem(key);
            // Parse stored json or if none return initialValue
            return item ? JSON.parse(item) : initialValue;
        } catch (e) {
            // If error also return initialValue
            log("edulla-chat", (e as Error).message, "visitor", { sessionId: "" })
            return initialValue;
        }
    });

    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    // useCallback ensures setValue has a stable reference across renders,
    // preventing fetch effects that depend on it from firing on every render.
    const setValue = useCallback((value: T | ((prev: T) => T)) => {
        try {
            // Allow value to be a function so we have same API as useState
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            // Save state
            setStoredValue(valueToStore);
            // Save to local storage
            if (typeof window !== "undefined") {
                window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
            }
        } catch (e) {
            // A more advanced implementation would handle the error case
            log("edulla-chat", (e as Error).message, "visitor", { sessionId: "" })
        }
    }, [key, storedValue]);

    return [storedValue, setValue] as const;
}
